import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { WorkPermit } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf, drawPill, SEMANTIC_HEX } from "../../../Shared/pdfCharts";
import "./WorkPermitsDashboard.scss";

const wpController = new WorkPermit();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});

const MONTH_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

const TIPO_KEYS = [
  "altura", "caliente", "espacio_confinado", "electrico_loto", "izaje",
  "quimicos", "excavacion", "caldera", "demolicion", "acceso_techo", "radiaciones",
];

const STATUS_COLOR = {
  draft:        "grey",
  requested:    "yellow",
  pending_n2:   "yellow",
  pending_n3:   "yellow",
  pending_n4:   "yellow",
  approved:     "blue",
  in_execution: "teal",
  verified:     "teal",
  closed:       "green",
  rejected:     "red",
  expired:      "red",
  suspended:    "orange",
  paused:       "orange",
};

const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
const DONUT_PALETTE = ["#C4D693", "#A3B76B", "#8CCAFF", "#001936"];
const MONTH_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const MONTH_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };

function EmptyMsg({ text = "Sin datos" }) {
  return <div className="wpdash__empty">{text}</div>;
}

const FilterDropdown = ({ options, value, onChange, placeholder, clearable = false }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla, no entra y se corta contra el viewport. Si no
  // hay lugar debajo pero sí arriba, abre para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const hasValue = clearable && Boolean(value);

  return (
    <div className="wpdash-fd" ref={ref}>
      <button type="button" className="wpdash-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "wpdash-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="wpdash-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`wpdash-fd-arrow${open ? " wpdash-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`wpdash-fd-menu${openUp ? " wpdash-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`wpdash-fd-item${String(o.value) === String(value) ? " wpdash-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function formatDate(date) {
  if (!date) return "-";
  const str = typeof date === "string" ? date : date.toISOString();
  const [y, m, d] = str.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

export function WorkPermitsDashboard({ site: siteProp, companyName, siteName }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const { site: contextSite } = useSite();
  const { company } = useCompany();
  const t = (key) => translations[key] || key;

  const site = siteProp !== undefined ? siteProp : contextSite;

  const [internalYear, setInternalYear] = useState(CURRENT_YEAR);
  const kpiRef    = useRef();
  const tableRef  = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  const [items, setItems] = useState(null);

  // Filters
  const [filterSearch, setFilterSearch] = useState("");
  const [filterType,   setFilterType]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const params = { active: true };
        if (site) params.site = site;
        else if (company) params.company = company;
        const res = await wpController.getWorkPermits(accessToken, params);
        setItems(Array.isArray(res) ? res : []);
      } catch {
        setItems([]);
      }
    })();
  }, [site, company, accessToken]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((p) => {
      const d = p.startDateTime || p.createdAt;
      if (!d) return false;
      return new Date(d).getFullYear() === internalYear;
    });
  }, [items, internalYear]);

  const kpis = useMemo(() => {
    const total       = filtered.length;
    const cerrados    = filtered.filter((p) => p.status === "closed").length;
    const activos     = filtered.filter((p) => p.status === "approved" || p.status === "in_execution").length;
    const pendientes  = filtered.filter((p) => p.status === "requested").length;
    const cumplimiento = total > 0 ? Math.round((cerrados / total) * 100) : 0;
    return { total, cerrados, activos, pendientes, cumplimiento };
  }, [filtered]);

  const displayItems = useMemo(() => {
    return filtered.filter((p) => {
      if (filterType   && !(p.tipos || []).includes(filterType)) return false;
      if (filterStatus && p.status !== filterStatus)             return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const tiposStr = (p.tipos || []).map(tipo => t(`wp_type_${tipo}`)).join(" ").toLowerCase();
        const matches =
          String(p.customId || "").includes(q) ||
          (p.workExecutor || "").toLowerCase().includes(q) ||
          tiposStr.includes(q) ||
          (p.area?.name || "").toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [filtered, filterSearch, filterType, filterStatus]);

  // Chart: by type
  const typeData = useMemo(() => {
    const counts = {};
    filtered.forEach((p) => {
      (p.tipos || []).forEach((tipo) => {
        const key = t(`wp_type_${tipo}`);
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filtered]);

  // Chart: by month
  const monthData = useMemo(() => {
    const counts = Array(12).fill(0);
    filtered.forEach((p) => {
      const d = p.startDateTime || p.createdAt;
      if (d) counts[new Date(d).getMonth()]++;
    });
    return MONTH_KEYS.map((key, idx) => ({ mes: t(key), value: counts[idx] }));
  }, [filtered]);

  // Chart: by status
  const statusData = useMemo(() => {
    const counts = {};
    filtered.forEach((p) => {
      const key = p.status ? t(p.status) : "Sin estado";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filtered]);

  const pieConfig = (data) => {
    const colors = data.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]);
    const total  = data.reduce((sum, d) => sum + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        title: "type",
        items: [{
          field: "value",
          name: "Cantidad",
          valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
        }],
      },
      height: 250,
    };
  };

  const colMonthConfig = {
    data: monthData,
    xField: "mes",
    yField: "value",
    colorField: "mes",
    scale: { color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X },
    legend: false,
    height: 250,
    style: MONTH_BAR_STYLE,
    axis: { x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS },
    tooltip: { items: [{ channel: "y", name: t("wpd_permits_tooltip") }] },
    interaction: MONTH_BAR_INTERACTION,
  };

  const hasMonthData = monthData.some((d) => d.value > 0);

  const TYPE_OPTIONS = TIPO_KEYS.map((k) => ({ key: k, value: k, text: t(`wp_type_${k}`) }));

  const STATUS_OPTIONS = [
    "draft", "requested", "pending_n2", "pending_n3", "pending_n4",
    "approved", "in_execution", "verified", "suspended", "paused",
    "rejected", "expired", "closed",
  ].map((s) => ({ key: s, value: s, text: t(s) }));

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const typeColorMap = Object.fromEntries(typeData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));
      const statusColorMap = Object.fromEntries(statusData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));

      const [typeDonut, monthColumn, statusDonut] = await Promise.all([
        renderDonutForPdf(typeData, typeColorMap, { showPercentLabels: true }),
        renderColumnForPdf(monthData, { xField: "mes", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 }),
        renderDonutForPdf(statusData, statusColorMap, { showPercentLabels: true }),
      ]);
      [typeDonut, monthColumn, statusDonut].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const tableColumns = [
        // Con "Total Grupo" (sin sitio puntual elegido) los datos vienen de
        // varios sitios mezclados — sin esta columna no se puede saber a
        // cuál pertenece cada fila (igual que en la tabla en pantalla).
        ...(!site ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
        { key: "permit", header: t("wpd_col_permit"), flex: 1, value: (r) => (r.customId ? `#${r.customId}` : "-") },
        {
          key: "workType", header: t("wpd_work_type"), flex: 1.6,
          value: (r) => ((r.tipos || []).length ? r.tipos.map((tipo) => t(`wp_type_${tipo}`)).join(", ") : "-"),
        },
        { key: "area", header: t("area"), flex: 1.2, value: (r) => r.area?.name || "-" },
        { key: "executor", header: t("wpd_col_executor"), flex: 1.3, value: (r) => r.workExecutor || "-" },
        { key: "start", header: t("wpd_col_start"), flex: 1, value: (r) => formatDate(r.startDateTime) },
        { key: "end", header: t("wpd_col_end_estimated"), flex: 1, value: (r) => formatDate(r.estimatedEndDateTime) },
        {
          key: "status", header: t("status"), flex: 1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            if (!row.status) return;
            drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(row.status), color: SEMANTIC_HEX[STATUS_COLOR[row.status]] || SEMANTIC_HEX.grey });
          },
        },
      ];

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("work_permits")} — ${internalYear}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          {
            height: 210,
            cards: [
              { title: t("wpd_by_work_type"), ...typeDonut, legendPosition: "right" },
              { title: t("wpd_permits_by_month"), ...monthColumn },
              { title: t("wpd_by_status"), ...statusDonut, legendPosition: "right" },
            ],
          },
        ],
        table: { columns: tableColumns, rows: displayItems, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
        tableTitle: t("work_permits"),
        filename: `permisos_trabajo_${internalYear}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="wpdash">
      {/* ── Filtros — afuera de la card gris de KPIs, arriba de todo (igual
          que los demás dashboards) ── */}
      <div className="wpdash__kpi-header">
        <YearBar year={internalYear} options={YEAR_OPTIONS} onChange={setInternalYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      {/* KPI wrapper */}
      <div className="resumen-wrapper wpdash__kpi-wrapper">
        <div className="wpdash__kpis" ref={kpiRef}>
          <KPICard label={t("wpd_total_permits")} value={kpis.total} />
          <KPICard
            label={t("wpd_closed")}
            value={`${kpis.cumplimiento}%`}
            rawValue={kpis.cumplimiento}
            showProgress
            progressColor="blue"
          />
          <KPICard label={t("wpd_active")} value={kpis.activos} subtitle={t("wpd_active_sub")} />
          <KPICard label={t("wpd_pending_approval")} value={kpis.pendientes} />
        </div>
      </div>

      {/* Charts — mismo patrón que los demás dashboards */}
      <div className="wpdash__charts">
        <div className="wpdash__chart-slot">
          <ChartBox title={t("wpd_by_work_type")}>
            {typeData.length > 0 ? (
              <div className="wpdash__pie-row">
                <div className="chartWrapper wpdash__pie-wrapper">
                  <Pie {...pieConfig(typeData)} />
                </div>
                <ChartLegend items={typeData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper wpdash__pie-wrapper"><div className="wpdash__empty-ring" /></div>
            )}
          </ChartBox>
        </div>

        <div className="wpdash__chart-slot">
          <ChartBox title={t("wpd_permits_by_month")}>
            <div className="chartWrapper">
              {hasMonthData ? <Column {...colMonthConfig} /> : <EmptyMsg text={t("no_data")} />}
            </div>
          </ChartBox>
        </div>

        <div className="wpdash__chart-slot">
          <ChartBox title={t("wpd_by_status")}>
            {statusData.length > 0 ? (
              <div className="wpdash__pie-row">
                <div className="chartWrapper wpdash__pie-wrapper">
                  <Pie {...pieConfig(statusData)} />
                </div>
                <ChartLegend items={statusData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper wpdash__pie-wrapper"><div className="wpdash__empty-ring" /></div>
            )}
          </ChartBox>
        </div>
      </div>

      {/* Filtros + tabla — mismo envoltorio gris que los demás dashboards */}
      <div className="wpdash__data-wrap">
      <div className="wpdash__filters">
        <div className="wpdash__search-wrapper">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="wpdash__search-input"
            placeholder={t("wpd_search_placeholder")}
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
          />
        </div>
        <div className="wpdash__filter-select">
          <FilterDropdown options={TYPE_OPTIONS} value={filterType} onChange={setFilterType} placeholder={t("wpd_work_type")} clearable />
        </div>
        <div className="wpdash__filter-select">
          <FilterDropdown options={STATUS_OPTIONS} value={filterStatus} onChange={setFilterStatus} placeholder={t("status")} clearable />
        </div>
      </div>

      {/* Table */}
      <div className="wpdash__table-section" ref={tableRef}>
        <div className="wpdash__ro-table-wrapper">
          <Table className="wpdash__ro-table unstackable rtable-cards">
            <Table.Header>
              <Table.Row>
                {!site && <Table.HeaderCell>{t("site")}</Table.HeaderCell>}
                <Table.HeaderCell>{t("wpd_col_permit")}</Table.HeaderCell>
                <Table.HeaderCell>{t("wpd_work_type")}</Table.HeaderCell>
                <Table.HeaderCell>{t("area")}</Table.HeaderCell>
                <Table.HeaderCell>{t("wpd_col_executor")}</Table.HeaderCell>
                <Table.HeaderCell>{t("wpd_col_start")}</Table.HeaderCell>
                <Table.HeaderCell>{t("wpd_col_end_estimated")}</Table.HeaderCell>
                <Table.HeaderCell>{t("status")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {displayItems.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={site ? 7 : 8} textAlign="center" style={{ color: "#999", padding: "1.5rem" }}>
                    {t("no_results")}
                  </Table.Cell>
                </Table.Row>
              ) : displayItems.map((p) => (
                <Table.Row key={p._id}>
                  {!site && <Table.Cell data-label={t("site")}>{p.site?.name || "-"}</Table.Cell>}
                  <Table.Cell data-label={t("wpd_col_permit")}>{p.customId ? `#${p.customId}` : "-"}</Table.Cell>
                  <Table.Cell data-label={t("wpd_work_type")} style={{ textAlign: "left" }}>
                    {(p.tipos || []).length
                      ? (p.tipos).map((tipo) => t(`wp_type_${tipo}`)).join(", ")
                      : "-"}
                  </Table.Cell>
                  <Table.Cell data-label={t("area")}>{p.area?.name || "-"}</Table.Cell>
                  <Table.Cell data-label={t("wpd_col_executor")}>{p.workExecutor || "-"}</Table.Cell>
                  <Table.Cell data-label={t("wpd_col_start")}>{formatDate(p.startDateTime)}</Table.Cell>
                  <Table.Cell data-label={t("wpd_col_end_estimated")}>{formatDate(p.estimatedEndDateTime)}</Table.Cell>
                  <Table.Cell data-label={t("status")}>
                    {p.status ? (
                      <span className="wpdash__row-pill" style={{ backgroundColor: SEMANTIC_HEX[STATUS_COLOR[p.status]] || SEMANTIC_HEX.grey, color: "#fff" }}>
                        {t(p.status)}
                      </span>
                    ) : "-"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </div>
      </div>
    </div>
  );
}
